/**
 * @fileoverview Componente responsável por renderizar conteúdo Markdown com estilos consistentes.
 */

import React, { useState } from "react";
import Markdown from "markdown-to-jsx";
import { makeStyles } from "@material-ui/core/styles";
import { Menu, MenuItem, ListItemIcon, ListItemText, Tooltip } from "@material-ui/core";
import { toast } from "react-toastify";
import StartConversationModal from "../StartConversationModal";

import { Copy, MessageCircle } from "lucide-react";
import { formatPhoneNumber } from "../../utils/formatPhoneNumber";
import { copyToClipboard } from "../../utils/copyToClipboard";
import { i18n } from "../../translate/i18n";
import usePermission from "../../hooks/usePermission";

// Componente visual da menção: verde, bold leve, cursor help + tooltip com número.
const MentionSpan = ({ label, number }) => (
  <Tooltip title={formatPhoneNumber(number)} arrow placement="top">
    <span
      style={{
        color: "#1C9654",
        fontWeight: 600,
        cursor: "help",
        padding: "0 2px",
        borderRadius: 3
      }}
    >
      {label}
    </span>
  </Tooltip>
);

const useStyles = makeStyles((theme) => ({
	link: {
		color: '#1C9654 !important', // Verde do WhatsApp
		textDecoration: 'underline',
		cursor: 'pointer',
		'&:hover': {
			color: '#128C7E !important', // Verde mais escuro no hover
		},
		'&:visited': {
			color: '#1C9654 !important', // Mantém verde mesmo depois de visitado
		}
	},
	phoneLink: {
		color: '#1C9654',
		textDecoration: 'underline',
		cursor: 'pointer',
		fontWeight: 500,
		padding: '1px 2px',
		borderRadius: 3,
		'&:hover': {
			color: '#128C7E',
			backgroundColor: 'rgba(28, 150, 84, 0.1)',
		}
	},
	phoneMenu: {
		'& .MuiPaper-root': {
			background: theme.palette.type === 'dark'
				? 'linear-gradient(135deg, rgba(18, 18, 19, 0.98) 0%, rgba(26, 26, 28, 0.98) 100%)'
				: '#ffffff',
			borderRadius: 12,
			border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.2)' : 'none',
			boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
		}
	}
}));

const elements = [
	"a",
	"abbr",
	"address",
	"area",
	"article",
	"aside",
	"audio",
	"b",
	"base",
	"bdi",
	"bdo",
	"big",
	"blockquote",
	"body",
	"br",
	"button",
	"canvas",
	"caption",
	"cite",
	"code",
	"col",
	"colgroup",
	"data",
	"datalist",
	"dd",
	"del",
	"details",
	"dfn",
	"dialog",
	"div",
	"dl",
	"dt",
	"em",
	"embed",
	"fieldset",
	"figcaption",
	"figure",
	"footer",
	"form",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"head",
	"header",
	"hgroup",
	"hr",
	"html",
	"i",
	"iframe",
	"img",
	"input",
	"ins",
	"kbd",
	"keygen",
	"label",
	"legend",
	"li",
	"link",
	"main",
	"map",
	"mark",
	"marquee",
	"menu",
	"menuitem",
	"meta",
	"meter",
	"nav",
	"noscript",
	"object",
	"ol",
	"optgroup",
	"option",
	"output",
	"p",
	"param",
	"picture",
	"pre",
	"progress",
	"q",
	"rp",
	"rt",
	"ruby",
	"s",
	"samp",
	"script",
	"section",
	"select",
	"small",
	"source",
	"span",
	"strong",
	"style",
	"sub",
	"summary",
	"sup",
	"table",
	"tbody",
	"td",
	"textarea",
	"tfoot",
	"th",
	"thead",
	"time",
	"title",
	"tr",
	"track",
	"u",
	"ul",
	"var",
	"video",
	"wbr",

	// SVG
	"circle",
	"clipPath",
	"defs",
	"ellipse",
	"foreignObject",
	"g",
	"image",
	"line",
	"linearGradient",
	"marker",
	"mask",
	"path",
	"pattern",
	"polygon",
	"polyline",
	"radialGradient",
	"rect",
	"stop",
	"svg",
	"text",
	"tspan",
];

const allowedElements = ["a", "b", "strong", "em", "u", "code", "del"];

const CustomLink = ({ children, ...props }) => {
	const classes = useStyles();
	return (
		<a
			{...props}
			target="_blank"
			rel="noopener noreferrer"
			className={classes.link}
		>
			{children}
		</a>
	);
};

// Componente para número de telefone clicável
const PhoneNumber = ({ number }) => {
	const classes = useStyles();
	// Conversar com o número cria o contato, se novo, e a conversa: hoje, aberto
	// a todos; com a regra nova, contacts.edit e tickets.create.
	const { can } = usePermission();
	const canStart = can("tickets.create", true) && can("contacts.edit", true);
	const [anchorEl, setAnchorEl] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);

	const handleClick = (event) => {
		event.preventDefault();
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleCopy = async () => {
		handleClose();
		if (await copyToClipboard(number)) toast.success(i18n.t("startConversationModal.copied"));
		else toast.error(i18n.t("startConversationModal.copyFailed"));
	};

	const handleStartConversation = () => {
		handleClose();
		setModalOpen(true);
	};

	return (
		<>
			<span
				className={classes.phoneLink}
				onClick={handleClick}
			>
				{number}
			</span>
			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleClose}
				className={classes.phoneMenu}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				getContentAnchorEl={null}
			>
				{canStart && (
					<MenuItem onClick={handleStartConversation}>
						<ListItemIcon>
							<MessageCircle size={18} style={{ color: '#25d366' }} />
						</ListItemIcon>
						<ListItemText primary={`Conversar com ${number}`} />
					</MenuItem>
				)}
				<MenuItem onClick={handleCopy}>
					<ListItemIcon>
						<Copy size={18} />
					</ListItemIcon>
					<ListItemText primary="Copiar número de telefone" />
				</MenuItem>
			</Menu>
			<StartConversationModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				phoneNumber={number}
			/>
		</>
	);
};

// Regex para detectar números de telefone (9+ dígitos, com ou sem formatação)
// Aceita formatos: 11999999999, +5511999999999, (11) 99999-9999, 11 99999-9999, etc
const phoneRegex = /(?:\+?\d{1,3}[-.\s]?)?(?:\(?\d{2,3}\)?[-.\s]?)?\d{4,5}[-.\s]?\d{4,5}/g;

// Função para verificar se um match é realmente um telefone válido (mínimo 9 dígitos)
const isValidPhone = (match) => {
	const digitsOnly = match.replace(/\D/g, '');
	return digitsOnly.length >= 9 && digitsOnly.length <= 15;
};

// Função para processar o texto e substituir números por componentes clicáveis
const processPhoneNumbers = (text) => {
	if (!text || typeof text !== 'string') return text;

	const parts = [];
	let lastIndex = 0;
	let match;

	// Reset regex lastIndex
	phoneRegex.lastIndex = 0;

	while ((match = phoneRegex.exec(text)) !== null) {
		const phoneMatch = match[0];

		// Verificar se é um número válido
		if (!isValidPhone(phoneMatch)) continue;

		// Adicionar texto antes do número
		if (match.index > lastIndex) {
			parts.push(text.substring(lastIndex, match.index));
		}

		// Adicionar o componente de telefone
		parts.push(
			<PhoneNumber key={`phone-${match.index}`} number={phoneMatch} />
		);

		lastIndex = match.index + phoneMatch.length;
	}

	// Adicionar texto restante
	if (lastIndex < text.length) {
		parts.push(text.substring(lastIndex));
	}

	// Se não encontrou nenhum número, retornar texto original
	if (parts.length === 0) return text;

	return parts;
};

const MarkdownWrapper = ({ children, mentionedJid, mentionsMeta }) => {
	// Conteúdo do bold/strike não pode começar nem terminar com whitespace,
	// nem conter quebra de linha — regra real do WhatsApp. Isso impede que
	// listas (`* item\n* item`) sejam interpretadas como bold entre linhas.
	const boldRegex = /\*(\S(?:[^*\n]*\S)?)\*/g;
	const tildaRegex = /~(\S(?:[^~\n]*\S)?)~/g;

	// Hook SEMPRE executa primeiro (regra do React) — não pode vir após early return.
	const options = React.useMemo(() => {
		const markdownOptions = {
			disableParsingRawHTML: true,
			forceInline: true,
			overrides: {
				a: { component: CustomLink },
			},
		};

		elements.forEach(element => {
			if (!allowedElements.includes(element)) {
				markdownOptions.overrides[element] = el => el.children || null;
			}
		});

		return markdownOptions;
	}, []);

	let processedText = children;

	// Se a mensagem tem menções geradas pelo sistema (outbound), renderiza
	// cada label "@Nome" como <MentionSpan> em verde com tooltip do número.
	// Partes entre menções caem no fluxo normal de Markdown + telefones.
	if (
		processedText &&
		typeof processedText === "string" &&
		Array.isArray(mentionsMeta) &&
		mentionsMeta.length > 0
	) {
		const labels = mentionsMeta
			.filter((m) => m && m.label)
			// Ordena por tamanho desc — evita que "@Maria" consuma "@Maria_Luiza"
			.sort((a, b) => (b.label.length || 0) - (a.label.length || 0));
		const byLabel = new Map(labels.map((m) => [m.label, m]));
		const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		const pattern = new RegExp(
			`(${labels.map((m) => escapeRegex(m.label)).join("|")})`,
			"g"
		);
		const chunks = processedText.split(pattern);
		if (chunks.length > 1) {
			return (
				<>
					{chunks.map((chunk, idx) => {
						const meta = byLabel.get(chunk);
						if (meta) {
							return <MentionSpan key={idx} label={meta.label} number={meta.number} />;
						}
						if (!chunk) return null;
						return (
							<MarkdownWrapper key={idx} mentionedJid={mentionedJid}>
								{chunk}
							</MarkdownWrapper>
						);
					})}
				</>
			);
		}
	}

	// ✅ Processar menções ANTES do markdown
	// Formato no WAHA: body = "@120306446393465 teste", mentionedJid = ["120306446393465@lid"]
	// Queremos transformar @120306446393465 em @Nome
	if (processedText && mentionedJid && Array.isArray(mentionedJid) && mentionedJid.length > 0) {
		// Extrair apenas os números dos JIDs mencionados (remover @c.us, @s.whatsapp.net, @lid, etc)
		const mentionedNumbers = mentionedJid.map(jid => {
			const match = jid.match(/^(\d+)@/);
			return match ? match[1] : null;
		}).filter(Boolean);

		// Substituir cada @numero pelos números encontrados
		mentionedNumbers.forEach(number => {
			const regex = new RegExp(`@${number}\\b`, 'g');
			// Substituir por formato especial que o markdown vai renderizar com estilo
			processedText = processedText.replace(regex, `**@${number}**`);
		});
	}

	if (processedText && boldRegex.test(processedText)) {
		processedText = processedText.replace(boldRegex, "**$1**");
	}
	if (processedText && tildaRegex.test(processedText)) {
		processedText = processedText.replace(tildaRegex, "~~$1~~");
	}

	if (!processedText) return null;

	// Renderizar markdown primeiro
	const markdownContent = <Markdown options={options}>{processedText}</Markdown>;

	// Agora processar números de telefone no resultado
	// Como o Markdown retorna um elemento React, precisamos processar de forma diferente
	// Vamos processar o texto original para detectar números antes do markdown

	// Verificar se há números de telefone no texto
	phoneRegex.lastIndex = 0;
	const hasPhones = phoneRegex.test(processedText);

	if (!hasPhones) {
		return markdownContent;
	}

	// Se tem telefones, processar o texto para incluir os componentes clicáveis
	// Precisamos fazer isso de forma que preserve o markdown
	const processedWithPhones = processPhoneNumbers(processedText);

	// Se processPhoneNumbers retornou array (tem números), renderizar com fragmentos
	if (Array.isArray(processedWithPhones)) {
		return (
			<>
				{processedWithPhones.map((part, index) => {
					if (typeof part === 'string') {
						return <Markdown key={index} options={options}>{part}</Markdown>;
					}
					return part; // Já é um componente PhoneNumber
				})}
			</>
		);
	}

	return markdownContent;
};

export default MarkdownWrapper;
